import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController } from 'chart.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChartConfiguration = any;
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import { PoModal } from '../components/PoModal';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';
import DateRangePicker from '../components/DateRangePicker';
import { fmtMoney, fmtMoney2, fmtDate } from '../lib/fmt';
import { MoneyInput } from '../components/inputs/MoneyInput';
import { paginateRows } from '../lib/table';
import { PO_STATUS_COLOR } from '../lib/colors';
import { getCurrentMonthRange } from '../lib/dateHelpers';
import { exportTableAsExcel } from '../lib/export';
import type { CronogramaOut, ItemOut, PagamentoParcelaOut, PaginatedResponse, ParcelaOut, PedidoDatasDisponiveisOut, PedidoOut, PedidoStatus } from '../types/api';

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController);
Chart.defaults.color = '#8A9BB8';
Chart.defaults.borderColor = '#22324F';

function CanvasChart({ id, cfg }: { id: string; cfg: ChartConfiguration }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const inst = useRef<Chart | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = new Chart(ref.current, cfg);
    return () => { inst.current?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cfg.data)]);
  return <canvas ref={ref} id={id} />;
}


const APPROVED_PO_STATUSES = new Set<PedidoStatus>(['Aprovado', 'Pago parcial', 'Pago total']);


function getDefaultAvailableRange(availableDates: string[]) {
  if (!availableDates.length) return getCurrentMonthRange();
  const now = new Date();
  const currentMonthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthDates = availableDates.filter((value) => value.startsWith(currentMonthPrefix));
  if (currentMonthDates.length) {
    return { from: currentMonthDates[0], to: currentMonthDates[currentMonthDates.length - 1] };
  }
  const latestDate = availableDates[availableDates.length - 1];
  const latestMonthPrefix = latestDate.slice(0, 7);
  const latestMonthDates = availableDates.filter((value) => value.startsWith(latestMonthPrefix));
  return { from: latestMonthDates[0], to: latestMonthDates[latestMonthDates.length - 1] };
}

type CronoSort = keyof ParcelaOut;

function exportCronoXLSX(parcelas: ParcelaOut[]) {
  const headers = ['Vencimento','Dia','Sem.','Pedido','Fornecedor','Parc.','Prazo (d)','Valor','Pago','Saldo','Status pagamento','Situação'];
  const today = new Date().toISOString().slice(0,10);
  const rows = parcelas.map(p => {
    const overdue = p.vencimento < today;
    const soon = !overdue && (new Date(p.vencimento + 'T00:00:00').getTime() - new Date().getTime()) <= 7*86400000;
    const situacao = overdue ? 'vencido' : soon ? 'em ≤7d' : 'a vencer';
    return [p.vencimento, p.dia_semana, `Sem ${p.semana_do_mes}`, p.numero_pedido, p.fornecedor, p.n, p.dias, p.valor, p.valor_pago, p.saldo, p.status_pagamento, situacao];
  });
  exportTableAsExcel(`MBC_cronograma_pagamentos_${new Date().toISOString().slice(0,10)}.xlsx`, headers, rows);
}

export default function Financeiro() {
  const qc = useQueryClient();
  const [periodRange, setPeriodRange] = useState(getCurrentMonthRange);
  const [err,setErr] = useState<string|null>(null);
  const [cronoSearch,setCronoSearch] = useState('');
  const [cronoSort,setCronoSort] = useState<CronoSort>('vencimento');
  const [cronoDir,setCronoDir] = useState<'asc'|'desc'>('asc');
  const [pedSort, setPedSort] = useState<'numero' | 'fornecedor' | 'valor_total' | 'condicao' | 'status_po' | 'created_at' | 'approved_at'>('created_at');
  const [pedDir, setPedDir] = useState<'asc' | 'desc'>('desc');
  const [pedPage, setPedPage] = useState(1);
  const [pedSearch, setPedSearch] = useState('');
  const [pedStatusFilter, setPedStatusFilter] = useState<'todos' | PedidoStatus>('todos');
  const [cronoPage, setCronoPage] = useState(1);
  const [showPoModal, setShowPoModal] = useState(false);
  const [editingPedido, setEditingPedido] = useState<PedidoOut | null>(null);
  const [paymentModal, setPaymentModal] = useState<{ parcela: ParcelaOut } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentObs, setPaymentObs] = useState('');
  const pedPageSize = 15;
  const cronoPageSize = 18;

  const { from: de, to: ate } = periodRange;
  const params = new URLSearchParams({limit:'500'});
  if(de) params.set('de',de);
  if(ate) params.set('ate',ate);

  const {data:pedidosPeriodoPage} = useQuery<PaginatedResponse<PedidoOut>>({
    queryKey:['pedidos', 'periodo', de, ate],
    queryFn:()=>api.get<PaginatedResponse<PedidoOut>>(`/pedidos?${params}`).then(r=>r.data),
  });
  const pedidosPeriodo = pedidosPeriodoPage?.items ?? [];

  const { data: pedidosDatasDisponiveis } = useQuery<PedidoDatasDisponiveisOut>({
    queryKey: ['pedidos', 'datas-disponiveis'],
    queryFn: () => api.get<PedidoDatasDisponiveisOut>('/pedidos/datas-disponiveis').then((r) => r.data),
  });
  const allowedDates = pedidosDatasDisponiveis?.datas ?? [];

  const pedidosFilaParams = new URLSearchParams({
    limit: String(pedPageSize),
    offset: String((pedPage - 1) * pedPageSize),
    sort_by: pedSort,
    sort_dir: pedDir,
  });
  if (de) pedidosFilaParams.set('de', de);
  if (ate) pedidosFilaParams.set('ate', ate);
  if (pedStatusFilter !== 'todos') pedidosFilaParams.set('status_po', pedStatusFilter);
  if (pedSearch.trim()) pedidosFilaParams.set('q', pedSearch.trim());

  const {data:pedidosFilaPage,isLoading:isLoadingFila} = useQuery<PaginatedResponse<PedidoOut>>({
    queryKey:['pedidos', 'fila', pedPage, pedPageSize, pedSort, pedDir, pedStatusFilter, pedSearch, de, ate],
    queryFn:()=>api.get<PaginatedResponse<PedidoOut>>(`/pedidos?${pedidosFilaParams.toString()}`).then(r=>r.data),
  });
  const pedidosFila = pedidosFilaPage?.items ?? [];

  const { data: itemsResponse } = useQuery<PaginatedResponse<ItemOut>>({
    queryKey: ['financeiro-items-modal'],
    queryFn: () => api.get<PaginatedResponse<ItemOut>>('/analysis/items?limit=2000').then(r => r.data),
  });
  const allItems = itemsResponse?.items ?? [];

  const {data:crono} = useQuery<CronogramaOut>({
    queryKey:['cronograma'],
    queryFn:()=>api.get<CronogramaOut>('/pedidos/cronograma').then(r=>r.data),
  });

  const { data: pagamentosParcela = [] } = useQuery<PagamentoParcelaOut[]>({
    queryKey: ['pagamentos-parcela', paymentModal?.parcela.id],
    enabled: Boolean(paymentModal?.parcela.id),
    queryFn: () => api.get<PagamentoParcelaOut[]>(`/pedidos/parcelas/${paymentModal!.parcela.id}/pagamentos`).then(r => r.data),
  });

  useEffect(() => {
    if (!allowedDates.length) return;
    if (!de && !ate) return;
    const allowedDateSet = new Set(allowedDates);
    if (allowedDateSet.has(de) && allowedDateSet.has(ate)) return;
    setPeriodRange(getDefaultAvailableRange(allowedDates));
    setPedPage(1);
  }, [allowedDates, ate, de]);

  const approveMut = useMutation({
    mutationFn:(id:number)=>api.post<PedidoOut>(`/pedidos/${id}/approve`),
    onSuccess:()=>{ qc.invalidateQueries({queryKey:['pedidos']}); qc.invalidateQueries({queryKey:['cronograma']}); },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const rejeitarMut = useMutation({
    mutationFn:(id:number)=>api.post<PedidoOut>(`/pedidos/${id}/rejeitar`),
    onSuccess:()=>{ qc.invalidateQueries({queryKey:['pedidos']}); qc.invalidateQueries({queryKey:['cronograma']}); },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const pagarParcelaMut = useMutation({
    mutationFn: ({ parcelaId, valor, pagoEm, observacoes }: { parcelaId: number; valor: number; pagoEm: string; observacoes?: string }) =>
      api.post<PagamentoParcelaOut>(`/pedidos/parcelas/${parcelaId}/pagamentos`, {
        valor,
        pago_em: pagoEm ? `${pagoEm}T12:00:00Z` : undefined,
        observacoes: observacoes || undefined,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey:['pedidos'] });
      qc.invalidateQueries({ queryKey:['cronograma'] });
      qc.invalidateQueries({ queryKey:['pagamentos-parcela', vars.parcelaId] });
      setPaymentModal(null);
      setPaymentAmount(0);
      setPaymentObs('');
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const estornarPagamentoMut = useMutation({
    mutationFn: ({ pagamentoId }: { pagamentoId: number }) => api.delete(`/pedidos/pagamentos/${pagamentoId}`),
    onSuccess: (_, { pagamentoId }) => {
      qc.invalidateQueries({ queryKey:['pedidos'] });
      qc.invalidateQueries({ queryKey:['cronograma'] });
      if (paymentModal) {
        qc.setQueryData(
          ['pagamentos-parcela', paymentModal.parcela.id],
          (old: PagamentoParcelaOut[] | undefined) => old?.filter(pg => pg.id !== pagamentoId) ?? [],
        );
        qc.invalidateQueries({ queryKey:['pagamentos-parcela', paymentModal.parcela.id] });
      }
      // modal permanece aberto para o usuário ver a lista atualizada
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });

  const aguardando = pedidosPeriodo.filter(p=>p.status_po==='Aguardando aprovação');
  const aprovados  = pedidosPeriodo.filter(p=>APPROVED_PO_STATUSES.has(p.status_po as PedidoStatus));
  const totalExpo  = pedidosPeriodo.reduce((s,p)=>s+Number(p.valor_total),0);
  const valAguard  = aguardando.reduce((s,p)=>s+Number(p.valor_total),0);

  const today = new Date(); today.setHours(0,0,0,0);
  const BUCK_KEYS = ['Vencido','0-7d','8-15d','16-30d','31-60d','60d+'];
  const buckets: Record<string,number> = Object.fromEntries(BUCK_KEYS.map(k=>[k,0]));
  pedidosPeriodo.forEach(p=>{
    const val=Number(p.valor_total);
    const dt=p.data_faturamento?new Date(p.data_faturamento):null;
    const days=dt?Math.round((dt.getTime()-today.getTime())/86400000):30;
    const b=days<0?'Vencido':days<=7?'0-7d':days<=15?'8-15d':days<=30?'16-30d':days<=60?'31-60d':'60d+';
    buckets[b]=(buckets[b]||0)+val;
  });

  const vencCfg: ChartConfiguration = {
    type:'bar',
    data:{ labels:BUCK_KEYS, datasets:[{label:'R$',data:BUCK_KEYS.map(k=>buckets[k]),backgroundColor:['#e0457b','#F47A20','#FB9D4B','#22a06b','#0A4D9E','#1E6FD6'],borderRadius:6}] },
    options:{ plugins:{legend:{display:false},tooltip:{callbacks:{label:(ctx:any)=>fmtMoney(ctx.raw as number)}}}, scales:{y:{ticks:{callback:(v:any)=>`R$${(Number(v)/1000).toFixed(0)}k`}}} },
  };

  const expoMap: Record<string,number> = {};
  pedidosPeriodo.forEach(p=>{ expoMap[p.fornecedor]=(expoMap[p.fornecedor]||0)+Number(p.valor_total); });
  const expoForn = Object.entries(expoMap).sort((a,b)=>b[1]-a[1]).slice(0,8);
  const expoFornCfg: ChartConfiguration = {
    type:'bar',
    data:{ labels:expoForn.map(e=>e[0]), datasets:[{label:'Exposição',data:expoForn.map(e=>e[1]),backgroundColor:'#0A4D9E',borderRadius:6}] },
    options:{ indexAxis:'y' as const, plugins:{legend:{display:false},tooltip:{callbacks:{label:(ctx:any)=>fmtMoney(ctx.raw as number)}}}, scales:{x:{ticks:{callback:(v:any)=>`R$${(Number(v)/1000).toFixed(0)}k`}}} },
  };

  // Cronograma data
  const todayStr = new Date().toISOString().slice(0,10);
  const parcelas = crono?.parcelas ?? [];
  const cronoFiltered = cronoSearch
    ? parcelas.filter(p =>
        p.fornecedor.toLowerCase().includes(cronoSearch.toLowerCase()) ||
        p.numero_pedido.toLowerCase().includes(cronoSearch.toLowerCase())
      )
    : parcelas;
  const cronoSorted = [...cronoFiltered].sort((a, b) => {
    const av = a[cronoSort] ?? '';
    const bv = b[cronoSort] ?? '';
    if (av < bv) return cronoDir === 'asc' ? -1 : 1;
    if (av > bv) return cronoDir === 'asc' ? 1 : -1;
    return 0;
  });
  const cronoPaged = paginateRows(cronoSorted, cronoPage, cronoPageSize);
  function handleCronoSort(k: CronoSort) {
    if (k === cronoSort) setCronoDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCronoSort(k); setCronoDir('asc'); }
    setCronoPage(1);
  }
  function handlePedSort(k: typeof pedSort) {
    if (k === pedSort) setPedDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setPedSort(k); setPedDir(k === 'numero' || k === 'fornecedor' || k === 'condicao' || k === 'status_po' ? 'asc' : 'desc'); }
    setPedPage(1);
  }

  function openPaymentModal(parcela: ParcelaOut) {
    setErr(null);
    setPaymentModal({ parcela });
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentObs('');
    setPaymentAmount(parcela.saldo > 0 ? parcela.saldo : 0);
  }

  function submitParcelaPagamento(mode: 'partial' | 'total') {
    if (!paymentModal) return;
    const raw = mode === 'total' ? paymentModal.parcela.saldo : paymentAmount;
    const valor = Math.round(raw * 100) / 100;
    if (!Number.isFinite(valor) || valor <= 0) {
      setErr('Informe um valor de pagamento válido.');
      return;
    }
    pagarParcelaMut.mutate({
      parcelaId: paymentModal.parcela.id,
      valor,
      pagoEm: paymentDate,
      observacoes: paymentObs,
    });
  }

  useEffect(() => {
    if (!paymentModal) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [paymentModal]);

  function SortTh({ label, k, cls='' }: { label: string; k: CronoSort; cls?: string }) {
    const active = cronoSort === k;
    return (
      <th className={`cursor-pointer select-none hover:text-mbc-soft ${cls}`} onClick={() => handleCronoSort(k)}>
        {label}{active && <span className="ml-1 text-mbc-orange">{cronoDir==='desc'?'↓':'↑'}</span>}
      </th>
    );
  }

  return (
    <div className="space-y-6 fade-in">

      {/* Importar PO */}
      <div className="card p-5 glow" style={{background:'linear-gradient(135deg,#16233B 0%,#0E1A2E 100%)',borderLeft:'4px solid #F47A20'}}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-4xl">📥</div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-white font-extrabold text-lg">Importar Pedido de Compra</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Anexe o PDF da nota/pedido para preencher os campos do POP e identificar os produtos. O cronograma financeiro é calculado automaticamente.</p>
          </div>
          <button
            type="button"
            onClick={() => { setEditingPedido(null); setShowPoModal(true); }}
            className="bg-mbc-orange hover:bg-mbc-orangel text-white font-bold px-6 py-3 rounded-xl shadow-lg pill-btn text-sm"
          >
            📥 Importar pedido
          </button>
        </div>
      </div>

      {/* 4 KPIs */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Exposição (em trânsito)" value={fmtMoney(totalExpo)}   sub={`${pedidosPeriodo.length} pedidos abertos`}  accent="mbc-orangel" icon="💳" />
        <KpiCard label="Pedidos a aprovar"        value={fmtMoney(valAguard)}   sub={`${aguardando.length} aguardando`}    accent="mbc-bluel"   icon="📝" />
        <KpiCard label="Aprovados"                value={fmtMoney(aprovados.reduce((s,p)=>s+Number(p.valor_total),0))} sub={`${aprovados.length} pedidos`} accent="emerald-300" icon="✅" />
        <KpiCard label="Divergências de prazo"    value="0"                     sub="pedidos fora da regra"               accent="rose-300"    icon="⚠️" />
      </div>

      {/* Gráficos */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Compras por vencimento (boletos)</h3>
          {pedidosPeriodo.length ? <CanvasChart id="cExpo" cfg={vencCfg}/> : <p className="text-mbc-mute text-sm py-10 text-center">Importe pedidos em aberto para ver a curva de vencimentos.</p>}
        </div>
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Exposição por fornecedor</h3>
          {expoForn.length ? <CanvasChart id="cExpoForn" cfg={expoFornCfg}/> : <p className="text-mbc-mute text-sm py-10 text-center">Nenhum pedido no período.</p>}
        </div>
      </div>

      {/* Fila de aprovação */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">📋 Consulta de pedidos</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Pedidos recém-salvos ficam com aprovação/rejeição. Depois de aprovados, o pagamento passa a ser controlado por parcela no cronograma.</p>
          </div>
          <span className="text-xs text-mbc-mute">{pedidosFilaPage?.total ?? 0} pedidos no resultado</span>
        </div>
        {err&&<p className="mb-3 text-rose-300 text-sm">{err}</p>}
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <DateRangePicker
            from={de}
            to={ate}
            allowedDates={allowedDates}
            onChange={(range) => {
              setPeriodRange(range);
              setPedPage(1);
            }}
            onClear={() => setPedPage(1)}
            className="w-full max-w-[360px]"
          />
          <input
            value={pedSearch}
            onChange={e => { setPedSearch(e.target.value); setPedPage(1); }}
            placeholder="Buscar número, fornecedor ou condição…"
            className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-mbc-soft placeholder:text-mbc-mute/70 w-72"
          />
          <select
            value={pedStatusFilter}
            onChange={e => { setPedStatusFilter(e.target.value as 'todos' | PedidoStatus); setPedPage(1); }}
            className="min-h-[38px] rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-1.5 text-sm text-mbc-soft focus:outline-none focus:border-mbc-orange"
          >
            <option value="todos">Todos os status</option>
            <option value="Aguardando aprovação">Aguardando aprovação</option>
            <option value="Aprovado">Aprovado</option>
            <option value="Rejeitado">Rejeitado</option>
            <option value="Pago parcial">Pago parcial</option>
            <option value="Pago total">Pago total</option>
            <option value="Recebido">Recebido</option>
          </select>
          <span className="pb-2 text-xs text-mbc-mute">{pedidosFilaPage?.total ?? 0} pedido{(pedidosFilaPage?.total ?? 0) !== 1 ? 's' : ''} na consulta</span>
        </div>
        <div className="scroller">
          {isLoadingFila ? <div className="p-8 text-center text-mbc-mute">Carregando…</div> :
          (pedidosFilaPage?.total ?? 0)===0 ? <div className="p-8 text-center text-mbc-mute">Nenhum pedido encontrado com os filtros atuais.</div> : (
            <table className="dt">
              <thead>
                <tr>
                  <SortableTh label="Nº / Ref." active={pedSort==='numero'} direction={pedDir} onClick={() => handlePedSort('numero')} style={{width:110}} />
                  <SortableTh label="Fornecedor" active={pedSort==='fornecedor'} direction={pedDir} onClick={() => handlePedSort('fornecedor')} className="text-left" style={{width:200}} />
                  <th className="num" style={{width:55}}>SKUs</th>
                  <SortableTh label="Valor total" active={pedSort==='valor_total'} direction={pedDir} onClick={() => handlePedSort('valor_total')} className="num" style={{width:130}} />
                  <SortableTh label="Condição" active={pedSort==='condicao'} direction={pedDir} onClick={() => handlePedSort('condicao')} className="text-center" style={{width:90}} />
                  <SortableTh label="Status" active={pedSort==='status_po'} direction={pedDir} onClick={() => handlePedSort('status_po')} className="text-center" style={{width:140}} />
                  <SortableTh label="Criado em" active={pedSort==='created_at'} direction={pedDir} onClick={() => handlePedSort('created_at')} className="num" style={{width:105}} />
                  <SortableTh label="Aprovado em" active={pedSort==='approved_at'} direction={pedDir} onClick={() => handlePedSort('approved_at')} className="num" style={{width:105}} />
                  <th className="text-center" style={{width:300}}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidosFila.map(p=>(
                  <tr key={p.id}>
                    <td>
                      <button
                        type="button"
                        onClick={() => { setEditingPedido(p); setShowPoModal(true); }}
                        className="font-mono text-mbc-bluel text-xs hover:text-mbc-orangel hover:underline"
                      >
                        {p.numero}
                      </button>
                    </td>
                    <td><span className="truncate inline-block max-w-[180px]" title={p.fornecedor}>{p.fornecedor}</span></td>
                    <td className="num">{p.skus_json.length}</td>
                    <td className="num font-semibold">{fmtMoney(Number(p.valor_total))}</td>
                    <td className="text-center text-mbc-mute">{p.condicao??'—'}</td>
                    <td className="text-center"><span className={`badge ${PO_STATUS_COLOR[p.status_po]??'bg-slate-500/20 text-slate-300'}`}>{p.status_po}</span></td>
                    <td className="num text-mbc-mute">{fmtDate(p.created_at)}</td>
                    <td className="num text-mbc-mute">{fmtDate(p.approved_at)}</td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => { setEditingPedido(p); setShowPoModal(true); }}
                          className="bg-mbc-blue/30 hover:bg-mbc-blue/50 text-mbc-bluel text-xs font-semibold px-3 py-1 rounded"
                        >
                          Editar
                        </button>
                        {APPROVED_PO_STATUSES.has(p.status_po as PedidoStatus) ? (
                          <>
                            <span className="badge bg-sky-500/15 text-sky-300">
                              Financeiro: {p.status_financeiro}
                            </span>
                            <button
                              onClick={()=>rejeitarMut.mutate(p.id)}
                              disabled={rejeitarMut.isPending}
                              className="text-xs font-semibold px-3 py-1 rounded disabled:opacity-40 text-white bg-rose-600/60 hover:bg-rose-600"
                            >
                              Rejeitar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={()=>approveMut.mutate(p.id)}
                              disabled={approveMut.isPending}
                              className="text-xs font-semibold px-3 py-1 rounded disabled:opacity-40 text-white bg-emerald-600 hover:bg-emerald-500"
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={()=>rejeitarMut.mutate(p.id)}
                              disabled={rejeitarMut.isPending}
                              className={`text-xs font-semibold px-3 py-1 rounded disabled:opacity-40 text-white ${
                                p.status_po==='Rejeitado'
                                  ? 'bg-rose-500'
                                  : 'bg-rose-600/60 hover:bg-rose-600'
                              }`}
                            >
                              Rejeitar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <TablePagination total={pedidosFilaPage?.total ?? 0} page={pedPage} pageSize={pedPageSize} onPageChange={setPedPage} />
      </div>

      {/* ── Cronograma de pagamentos ── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">🗓️ Cronograma de pagamentos</h3>
            <p className="text-xs text-mbc-mute mt-0.5">
              O cronograma agora controla o financeiro por parcela: valor previsto, valor pago, saldo e status de pagamento.
              {crono && crono.kpis.tot_pedidos_aprovados > 0 && (
                <> {crono.kpis.tot_parcelas} parcelas em {crono.kpis.tot_pedidos_aprovados} pedido{crono.kpis.tot_pedidos_aprovados !== 1 ? 's' : ''}.</>
              )}
              {crono && crono.kpis.tot_pedidos_aguardando > 0 && (
                <span className="text-amber-300"> · {crono.kpis.tot_pedidos_aguardando} aguardando aprovação.</span>
              )}
            </p>
          </div>
          {parcelas.length > 0 && (
            <button onClick={() => exportCronoXLSX(parcelas)} className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-2 rounded-lg transition-colors">
              📥 Exportar Excel
            </button>
          )}
        </div>

        {parcelas.length === 0 ? (
          <div className="card p-6 text-center text-mbc-mute text-sm">
            {crono?.kpis.tot_pedidos_aguardando
              ? <span className="text-amber-300">⏳ {crono.kpis.tot_pedidos_aguardando} pedido{crono.kpis.tot_pedidos_aguardando > 1 ? 's' : ''} aguardando aprovação. Aprove na fila acima para gerar o cronograma.</span>
              : 'Nenhum pedido aprovado ou pago com condição e data de faturamento cadastradas.'
            }
          </div>
        ) : (
          <>
            {/* Tabela de parcelas */}
            <div className="mb-3">
              <input
                value={cronoSearch}
                onChange={e => setCronoSearch(e.target.value)}
                placeholder="Buscar pedido ou fornecedor…"
                className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-mbc-soft placeholder:text-mbc-mute/70 w-64"
              />
            </div>
            <div className="scroller">
              <table className="dt">
                <thead>
                  <tr>
                    <SortTh label="Vencimento" k="vencimento" />
                    <SortTh label="Dia" k="dia_semana" cls="text-center" />
                    <SortTh label="Sem." k="semana_do_mes" cls="text-center" />
                    <SortTh label="Pedido" k="numero_pedido" />
                    <SortTh label="Fornecedor" k="fornecedor" />
                    <SortTh label="Parc." k="n" cls="text-center" />
                    <SortTh label="Prazo" k="dias" cls="num" />
                    <SortTh label="Valor" k="valor" cls="num" />
                    <th className="num" style={{ width: 110 }}>Pago</th>
                    <th className="num" style={{ width: 110 }}>Saldo</th>
                    <th className="text-center" style={{ width: 130 }}>Pagamento</th>
                    <th className="text-center" style={{ width: 110 }}>Situação</th>
                    <th className="text-center" style={{ width: 180 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {cronoSorted.length === 0 ? (
                    <tr><td colSpan={13} className="text-center text-mbc-mute py-6 text-sm">Nenhuma parcela encontrada.</td></tr>
                  ) : cronoPaged.map((p, i) => {
                    const overdue = p.vencimento < todayStr;
                    const soon = !overdue && (new Date(p.vencimento + 'T00:00:00').getTime() - Date.now()) <= 7 * 86400000;
                    const paymentBadge = p.status_pagamento === 'Pago'
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : p.status_pagamento === 'Parcial'
                        ? 'bg-sky-500/20 text-sky-300'
                        : 'bg-amber-500/20 text-amber-300';
                    return (
                      <tr key={`${p.pedido_id}-${p.n}-${i}`}>
                        <td>{new Date(p.vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                        <td className="text-center text-mbc-mute">{p.dia_semana}</td>
                        <td className="text-center">
                          <span className="badge bg-mbc-blue/25 text-mbc-bluel">Sem {p.semana_do_mes}</span>
                        </td>
                        <td><span className="font-mono text-mbc-bluel text-xs">{p.numero_pedido}</span></td>
                        <td><span className="truncate inline-block max-w-[200px]" title={p.fornecedor}>{p.fornecedor}</span></td>
                        <td className="text-center"><b>{p.n}</b></td>
                        <td className="num text-mbc-mute">{p.dias}d</td>
                        <td className="num font-semibold">{fmtMoney2(p.valor)}</td>
                        <td className="num text-sky-300">{fmtMoney2(p.valor_pago)}</td>
                        <td className="num text-amber-300">{fmtMoney2(p.saldo)}</td>
                        <td className="text-center">
                          <span className={`badge ${paymentBadge}`}>{p.status_pagamento}</span>
                        </td>
                        <td className="text-center">
                          {overdue
                            ? <span className="badge bg-rose-500/20 text-rose-300">vencido</span>
                            : soon
                              ? <span className="badge bg-amber-500/20 text-amber-300">em ≤7d</span>
                              : <span className="badge bg-emerald-500/15 text-emerald-300">a vencer</span>
                          }
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            onClick={() => openPaymentModal(p)}
                            className="rounded-md bg-mbc-orange/20 hover:bg-mbc-orange/40 px-3 py-1 text-[11px] text-mbc-orangel"
                          >
                            Pagar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination total={cronoSorted.length} page={cronoPage} pageSize={cronoPageSize} onPageChange={setCronoPage} />
          </>
        )}
      </div>

      {showPoModal && (
        <PoModal
          initialSkus={editingPedido?.skus_json ?? []}
          allItems={allItems}
          existingPedido={editingPedido}
          onClose={() => {
            setShowPoModal(false);
            setEditingPedido(null);
          }}
          onCreated={() => {
            setShowPoModal(false);
            setEditingPedido(null);
          }}
          onDeleted={() => {
            setShowPoModal(false);
            setEditingPedido(null);
          }}
        />
      )}

      {paymentModal && createPortal(
        (
          <div className="fixed inset-0 z-[80]">
            <div className="absolute inset-0 bg-black/78 backdrop-blur-[2px]" onClick={() => setPaymentModal(null)} />
            <div className="absolute inset-0 overflow-y-auto">
              <div className="flex min-h-full items-start justify-center p-4 pt-20 md:items-center md:p-6">
                <div className="card w-full max-w-xl rounded-2xl p-5 shadow-2xl max-h-[calc(100vh-2rem)] overflow-y-auto md:max-h-[85vh]">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Pagamentos da parcela {paymentModal.parcela.n}
                      </h3>
                      <p className="mt-1 text-xs text-mbc-mute">
                        Pedido <span className="font-mono text-mbc-bluel">{paymentModal.parcela.numero_pedido}</span> · {paymentModal.parcela.fornecedor} · Saldo <span className="text-amber-300">{fmtMoney2(paymentModal.parcela.saldo)}</span>
                      </p>
                    </div>
                    <button type="button" onClick={() => setPaymentModal(null)} className="text-2xl leading-none text-mbc-mute hover:text-white">×</button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs text-mbc-mute">Valor a baixar</label>
                      <MoneyInput value={paymentAmount} onChange={setPaymentAmount} />
                    </div>
                    <div>
                      <label className="text-xs text-mbc-mute">Data do pagamento</label>
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={e => setPaymentDate(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-mbc-mute">Observações</label>
                      <textarea
                        rows={2}
                        value={paymentObs}
                        onChange={e => setPaymentObs(e.target.value)}
                        placeholder="ex: pagamento parcial, complemento, ajuste bancário..."
                        className="mt-1 w-full resize-none rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-mbc-line bg-mbc-panel2/70 p-3">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-mbc-mute">Pagamentos já lançados</div>
                    {pagamentosParcela.length === 0 ? (
                      <div className="text-xs text-mbc-mute">Nenhuma baixa registrada nesta parcela.</div>
                    ) : (
                      <div className="space-y-2">
                        {pagamentosParcela.map(pg => (
                          <div key={pg.id} className="flex items-center justify-between gap-3 text-xs">
                            <span className="text-mbc-soft">{fmtDate(pg.pago_em)}{pg.observacoes ? ` · ${pg.observacoes}` : ''}</span>
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-emerald-300">{fmtMoney2(Number(pg.valor))}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setErr(null);
                                  estornarPagamentoMut.mutate({ pagamentoId: pg.id });
                                }}
                                disabled={estornarPagamentoMut.isPending}
                                className="rounded-md bg-rose-500/15 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/25 disabled:opacity-40"
                              >
                                estornar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {err && <p className="mt-3 text-sm text-rose-300">{err}</p>}

                  <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={() => setPaymentModal(null)} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-4 py-2 text-sm text-mbc-soft hover:text-white">
                      Cancelar
                    </button>
                    {paymentModal.parcela.saldo > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => submitParcelaPagamento('total')}
                          disabled={pagarParcelaMut.isPending}
                          className="rounded-xl bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40"
                        >
                          {pagarParcelaMut.isPending ? 'Salvando…' : `Quitar saldo (${fmtMoney2(paymentModal.parcela.saldo)})`}
                        </button>
                        <button
                          type="button"
                          onClick={() => submitParcelaPagamento('partial')}
                          disabled={pagarParcelaMut.isPending}
                          className="rounded-xl bg-mbc-orange px-4 py-2 text-sm font-semibold text-white hover:bg-mbc-orangel disabled:opacity-40"
                        >
                          {pagarParcelaMut.isPending ? 'Salvando…' : 'Registrar parcial'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

    </div>
  );
}
